export interface OfflineSyncStats {
  pendingReviews: number;
  pendingResponses: number;
  failedResponses: number;
  pendingOnDemandItems: number;
  syncErrors: number;
  pendingPhotos: number;
  failedPhotos: number;
  recoverableItems: number;
  recoveryAvailable: boolean;
  waitingToSyncCount: number;
  retryNeededCount: number;
  totalPending: number;
  lastSyncAttempt?: Date;
}

interface OfflineSyncStatusReview {
  id: number;
  propertyId?: number | null;
  syncStatus?: string | null;
  conflictStatus?: string | null;
  metadata?: Record<string, unknown> | null;
}

interface OfflineSyncStatusResponse {
  id?: number | null;
  syncStatus?: string | null;
  conflictStatus?: string | null;
}

interface OfflineSyncStatusPhotoNote {
  id: number;
  responseId?: number | null;
  noteSyncStatus?: string | null;
}

interface OfflineSyncStatusOnDemandItem {
  review_id: number;
  sync_status?: string | null;
}

interface OfflineSyncStatusRoom {
  property_id: number;
  sync_status?: string | null;
}

interface OfflineSyncStatusDatabaseStats {
  photosQueue: number;
}

interface OfflineSyncStatusRecoverableWorkItem {
  kind?: string | null;
  entityId?: number | null;
  syncStatus?: string | null;
}

export interface OfflineSyncStatusDatabase {
  getReviews(filter?: any): Promise<OfflineSyncStatusReview[]>;
  getReview(reviewId: number): Promise<OfflineSyncStatusReview | null>;
  getResponsesBySyncStatus(syncStatuses: string[]): Promise<any[]>;
  getPendingOfflineOnDemandItems(): Promise<OfflineSyncStatusOnDemandItem[]>;
  getPendingOfflineRooms(): Promise<OfflineSyncStatusRoom[]>;
  getPhotosFromQueue?(filter?: any): Promise<any[]>;
  getPendingPhotoNotes?(): Promise<OfflineSyncStatusPhotoNote[]>;
  getDatabaseStats(): Promise<OfflineSyncStatusDatabaseStats>;
  getResponsesByReviewId(reviewId: number): Promise<OfflineSyncStatusResponse[]>;
  updateReview(reviewId: number, updates: any): Promise<void>;
  getRecoverableWorkCount?(): Promise<number>;
  getRecoverableWorkItems?(): Promise<OfflineSyncStatusRecoverableWorkItem[]>;
}

const unavailableSyncStats = (): OfflineSyncStats => ({
  pendingReviews: 0,
  pendingResponses: 0,
  failedResponses: 0,
  pendingOnDemandItems: 0,
  syncErrors: 1,
  pendingPhotos: 0,
  failedPhotos: 0,
  recoverableItems: 0,
  recoveryAvailable: false,
  waitingToSyncCount: 0,
  retryNeededCount: 1,
  totalPending: 1,
});

const recoveryChoiceConflictStatuses = new Set(['local_wins', 'conflicted', 'restored']);
const activeSyncStatuses = new Set(['pending', 'syncing']);

const needsRecoveryChoice = (row: {
  syncStatus?: string | null;
  conflictStatus?: string | null;
}): boolean =>
  row.conflictStatus === 'conflicted' ||
  (recoveryChoiceConflictStatuses.has(row.conflictStatus || '') &&
    !activeSyncStatuses.has(row.syncStatus || ''));

export class OfflineSyncStatusService {
  constructor(
    private database: OfflineSyncStatusDatabase,
    private logger: Pick<Console, 'warn'> = console
  ) {}

  async getSyncStats(): Promise<OfflineSyncStats> {
    try {
      const pendingReviews = await this.database.getReviews({ syncStatus: 'pending' });
      const syncingReviews = await this.database.getReviews({ syncStatus: 'syncing' });
      const failedReviews = await this.database.getReviews({ syncStatus: 'failed' });
      const pendingResponseRows = await this.database.getResponsesBySyncStatus([
        'pending',
        'syncing',
      ]);
      const failedResponseRows = await this.database.getResponsesBySyncStatus(['failed']);
      const pendingOnDemandItems = await this.database.getPendingOfflineOnDemandItems();
      const unresolvedRooms = await this.database.getPendingOfflineRooms();
      const queuedPhotos = await this.database.getPhotosFromQueue?.({}) || [];
      const pendingPhotoNotes = this.database.getPendingPhotoNotes
        ? await this.database.getPendingPhotoNotes() || []
        : [];
      let recoverableItems = 0;
      let recoverableWorkItems: OfflineSyncStatusRecoverableWorkItem[] = [];
      if (this.database.getRecoverableWorkItems) {
        recoverableWorkItems = await this.database.getRecoverableWorkItems() ?? [];
        recoverableItems = recoverableWorkItems.length;
      } else if (this.database.getRecoverableWorkCount) {
        recoverableItems = await this.database.getRecoverableWorkCount() ?? 0;
      }

      const recoverablePendingResponseIds = new Set(
        recoverableWorkItems
          .filter(item =>
            item.kind === 'response' &&
            item.entityId != null &&
            activeSyncStatuses.has(item.syncStatus || '')
          )
          .map(item => item.entityId)
      );

      const pendingReviewRows = [...pendingReviews, ...syncingReviews]
        .filter(review => !needsRecoveryChoice(review));
      const failedReviewRows = failedReviews.filter(review => !needsRecoveryChoice(review));
      const pendingRows = pendingResponseRows.filter(response =>
        !needsRecoveryChoice(response) &&
        !recoverablePendingResponseIds.has(response.id ?? null)
      );
      const failedRows = failedResponseRows.filter(response => !needsRecoveryChoice(response));

      const pendingResponses = pendingRows.length;
      const failedResponses = failedRows.length;
      const waitingOnDemandItemCount = pendingOnDemandItems.filter(item =>
        item.sync_status !== 'failed'
      ).length;
      const failedOnDemandItemCount = pendingOnDemandItems.filter(item =>
        item.sync_status === 'failed'
      ).length;
      const waitingRoomCount = unresolvedRooms.filter(room =>
        room.sync_status !== 'failed'
      ).length;
      const failedRoomCount = unresolvedRooms.filter(room =>
        room.sync_status === 'failed'
      ).length;
      const pendingQueuedPhotos = queuedPhotos.filter(photo =>
        photo.status === 'pending' || photo.status === 'uploading'
      ).length;
      const failedQueuedPhotos = queuedPhotos.filter(photo => photo.status === 'failed').length;
      const pendingPhotoNoteCount = pendingPhotoNotes.filter(note =>
        note.noteSyncStatus !== 'failed'
      ).length;
      const failedPhotoNoteCount = pendingPhotoNotes.filter(note =>
        note.noteSyncStatus === 'failed'
      ).length;
      // Uploaded-photo note mutations are durable sync work too. Fold them into
      // the photo totals so the existing status UI remains truthful without a
      // separate presentation-only counter.
      const pendingPhotos = pendingQueuedPhotos + pendingPhotoNoteCount;
      const failedPhotos = failedQueuedPhotos + failedPhotoNoteCount;
      const waitingToSyncCount = (
        pendingReviewRows.length +
        pendingResponses +
        pendingPhotos +
        waitingOnDemandItemCount +
        waitingRoomCount
      );
      const retryNeededCount = (
        failedReviewRows.length +
        failedResponses +
        failedPhotos +
        failedOnDemandItemCount +
        failedRoomCount
      );
      const totalPending = waitingToSyncCount + retryNeededCount;

      return {
        pendingReviews: pendingReviewRows.length,
        pendingResponses,
        failedResponses,
        pendingOnDemandItems: waitingOnDemandItemCount,
        syncErrors: failedReviewRows.length + failedResponses + failedOnDemandItemCount + failedRoomCount,
        pendingPhotos,
        failedPhotos,
        recoverableItems,
        recoveryAvailable: recoverableItems > 0,
        waitingToSyncCount,
        retryNeededCount,
        totalPending,
      };
    } catch (error) {
      this.logger.warn('[OfflineReviewService] Error getting sync stats:', error);
      return unavailableSyncStats();
    }
  }

  async recomputeReviewSyncStatuses(): Promise<void> {
    const allReviews = await this.database.getReviews({});

    for (const review of allReviews) {
      await this.recomputeReviewSyncStatus(review.id, review);
    }
  }

  async recomputeReviewSyncStatus(
    reviewId: number,
    reviewSnapshot?: OfflineSyncStatusReview
  ): Promise<void> {
    try {
      const review = reviewSnapshot ?? await this.database.getReview(reviewId);
      // A failed review row represents unresolved review-level data. Child
      // queues being empty is insufficient evidence to mark it synced; the
      // next full-sync run must retry the review record first.
      if (review?.syncStatus === 'failed') {
        return;
      }
      const hasPendingLifecycleAction = Boolean(
        review?.metadata &&
        typeof review.metadata === 'object' &&
        review.metadata.pendingLifecycleAction
      );
      const responses = await this.database.getResponsesByReviewId(reviewId);
      const hasPendingResponses = responses.some(response =>
        response.syncStatus === 'pending' || response.syncStatus === 'failed' || response.syncStatus === 'syncing'
      );

      const pendingOnDemandItems = await this.database.getPendingOfflineOnDemandItems();
      const hasPendingItems = pendingOnDemandItems.some(item =>
        item.review_id === reviewId && item.sync_status !== 'synced'
      );
      const pendingRooms = await this.database.getPendingOfflineRooms();
      const hasPendingRooms = review?.propertyId != null && pendingRooms.some(room =>
        Number(room.property_id) === Number(review.propertyId) && room.sync_status !== 'synced'
      );
      const queuedPhotos = await this.database.getPhotosFromQueue?.({}) || [];
      const hasPendingPhotos = queuedPhotos.some(photo =>
        (photo.status === 'pending' || photo.status === 'uploading' || photo.status === 'failed') &&
        photo.metadata?.reviewId === reviewId
      );
      const responseIds = new Set(
        responses
          .map(response => response.id)
          .filter((responseId): responseId is number => responseId != null)
      );
      const pendingPhotoNotes = this.database.getPendingPhotoNotes
        ? await this.database.getPendingPhotoNotes() || []
        : [];
      const hasPendingPhotoNotes = pendingPhotoNotes.some(note =>
        note.responseId != null && responseIds.has(note.responseId)
      );

      if (
        !hasPendingLifecycleAction &&
        !hasPendingResponses &&
        !hasPendingItems &&
        !hasPendingRooms &&
        !hasPendingPhotos &&
        !hasPendingPhotoNotes
      ) {
        await this.database.updateReview(reviewId, {
          syncStatus: 'synced',
          syncError: undefined,
        });
      }
    } catch (error) {
      this.logger.warn('[OfflineReviewService] Failed to recompute review sync status:', error);
    }
  }
}
